<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType } from '@/types/equip'
import { computed, ref } from 'vue'
import { equipIcon, equipTypeList } from '../../item/data'
import EquipInfo from '../../item/components/EquipInfo.vue'
import { getEnchantPrice, getEnchantStone } from '../data'
import { socketEmitAsync } from '@/utils/web-socket'
import type { EnchantRes } from '@/types/smithy'
import { formatNumberWithCommas, formatNumber } from '@/utils'
import { ElMessage } from 'element-plus'
import { getEquipName } from '@/utils/equip'
import { QuestionFilled } from '@element-plus/icons-vue'

const userStore = useUserStore()

const equipItemId = ref('')
const equipInfo = computed(() => {
  const res = userStore.itemList.find((item) => item.id === equipItemId.value)
  if (!res) {
    equipItemId.value = ''
    return undefined
  }
  const { name, type, attrs } = equipCollection[res.equipId]
  return { ...res, name, type, attrs }
})
const equippedList = computed(() =>
  equipTypeList.value.map((item) => {
    const itemId = userStore.equippedList[item]
    const equipInfo = userStore.itemList.find((item) => item.id === itemId)
    if (!equipInfo) return undefined
    const { name, type, attrs } = equipCollection[equipInfo.equipId]
    return { ...equipInfo, name, type, attrs }
  }),
)
const cantEnchant = computed(() => Boolean(equipInfo.value?.enchantAttr?.[1] && equipInfo.value.enchantAttr[1] >= 1))

const itemList = computed<EquipItemType[]>(() => {
  const items = userStore.itemList
  if (userStore.itemList.length === 0) return []
  return items
    .filter((item) => {
      const equippedIdList = Object.values(userStore.equippedList)
      if (item.darkGoldAttrs || item.supremeAttrs) return
      return !equippedIdList.includes(item.id)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
})

function handleChoice(item: EquipItemType | undefined) {
  if (item?.darkGoldAttrs) return ElMessage.warning('暗金装备不可附魔')
  if (item?.supremeAttrs) return ElMessage.warning('至臻装备不可附魔')
  equipItemId.value = item?.id || ''
  isEnhance.value = false
}

const enchantResShow = ref(false)
function handleEnchant() {
  if (!equipInfo.value) return
  const { id, quality, name, enchantAttr } = equipInfo.value
  const price = getEnchantPrice(quality, enchantAttr)
  const stone = getEnchantStone(enchantAttr)
  if (price > userStore.gold) return ElMessage.warning('您的金币不足')
  if (userStore.consumables.crackStone < stone) return ElMessage.warning('您的裂隙晶石不足')
  ElMessageBox.confirm(`<p class="affix">确定附魔<span class="${quality}">${name}</span>吗？</p>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const { enchantAttr, userAttr } = await socketEmitAsync<EnchantRes>({ type: 'enchantEquip', data: { id, useEnhance: isEnhance.value } })
      userStore.userAttr = userAttr

      const item = userStore.itemList.find((item) => item.id === id)
      if (!item || !equipInfo.value) return
      item.enchantAttr = enchantAttr
      equipInfo.value.enchantAttr = enchantAttr

      enchantResShow.value = true

      userStore.gold -= price
      userStore.consumables.crackStone -= stone

      if (isEnhance.value) {
        userStore.consumables.enchantEnhance--
        if (userStore.consumables.enchantEnhance <= 0) isEnhance.value = false
      }
    })
    .catch(() => {})
}

const isEnhance = ref(false)
function beforeEnhanceChange() {
  if (equipInfo.value!.enchantAttr?.[0] !== 5) {
    ElMessage.warning('附魔等级 5级时才可使用增强剂')
    return false
  }
  if (!userStore.consumables.enchantEnhance) {
    ElMessage.warning('您未拥有附魔增强剂')
    return false
  }
  return true
}
</script>

<template>
  <div class="enchant">
    <div class="enchant-table border-wrap">
      <h4>锻造台</h4>
      <div class="enchant-equip">
        <CommonBtn style="width: 100%" @done="handleChoice(undefined)">
          <span v-if="equipInfo" v-html="getEquipName(equipInfo, equipInfo.type)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>附魔说明</h4>
            <p>1. 通过<span class="peerless">虚空裂隙</span>挑战，可在使用<span class="peerless">裂隙结晶</span>对装备进行附魔。</p>
            <p>2. 附魔后将获得<span class="grow">抗魔值</span>及<span class="grow">全伤害加成</span>。</p>
            <p>3. 随附魔等级增加，所需<span class="gold">金币</span>及<span class="peerless">裂隙结晶</span>也会增加。</p>
            <p>
              4. 在附魔等级达到 <span class="info">5</span>级时，再次附魔不再增加附魔等级，但将随机<span class="grow">全伤害加成</span
              >属性，每次附魔至少提升 <span class="grow">0.01%</span>（使用<span class="info">附魔增强剂</span>时为
              <span class="grow">0.05%</span>），当前版本中最高获得 <span class="grow">1% 全伤害加成</span>。
            </p>
            <p>5. 附魔加成如下表格所示：</p>
            <table border style="text-align: center; width: 100%">
              <tr>
                <td>附魔等级</td>
                <td>1</td>
                <td>2</td>
                <td>3</td>
                <td>4</td>
                <td>5</td>
              </tr>
              <tr>
                <td>抗魔值</td>
                <td>10</td>
                <td>20</td>
                <td>30</td>
                <td>40</td>
                <td>50</td>
              </tr>
              <tr>
                <td>全伤害加成</td>
                <td>0.1%</td>
                <td>0.2%</td>
                <td>0.3%</td>
                <td>0.4%</td>
                <td>0.5% ~ 1%</td>
              </tr>
            </table>
          </div>
        </el-popover>
      </div>
      <template v-if="equipInfo">
        <div class="info-card">
          <el-row>
            <el-col :span="12" style="display: flex; align-items: center">
              <p>附魔等级：{{ equipInfo.enchantAttr?.[0] || 0 }}</p>
            </el-col>
            <el-col :span="12">
              <p style="align-items: center; justify-content: flex-start">
                附魔增强（{{ userStore.consumables.enchantEnhance }}）：
                <el-switch v-model="isEnhance" size="small" :before-change="beforeEnhanceChange" />
              </p>
            </el-col>
          </el-row>
          <p>附魔加成：{{ formatNumber(equipInfo.enchantAttr?.[1], 2) || 0 }}%</p>
          <p>
            附魔花费：{{ formatNumberWithCommas(getEnchantPrice(equipInfo.quality, equipInfo.enchantAttr)) }} 金币 +
            {{ getEnchantStone(equipInfo.enchantAttr) }} 裂隙晶石
          </p>
        </div>
        <div class="btn-wrap">
          <el-button type="warning" plain @click="handleEnchant" :disabled="cantEnchant">
            {{ cantEnchant ? '已达附魔上限' : '开始附魔' }}
          </el-button>
        </div>
      </template>
    </div>
    <div class="item-list border-wrap" style="flex-shrink: 0; margin-bottom: 10px">
      <h4>已装备</h4>
      <div class="item-btn-wrap btn-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="(item, index) of equippedList" :key="index">
            <CommonBtn
              size="small"
              @done="handleChoice(item)"
              :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" v-html="getEquipName(item, index)"></span>
              <span v-else>{{ equipIcon[equipTypeList[index]] }} (未装备)</span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="item-list border-wrap package">
      <h4>背包</h4>
      <p class="cur-gold">
        <span>💰 {{ formatNumberWithCommas(userStore.gold) }}</span>
        <span style="margin-left: 10px">🔷 {{ formatNumberWithCommas(userStore.consumables.crackStone) }}</span>
      </p>
      <div class="item-btn-wrap btn-wrap item-list-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of itemList" :key="item.id">
            <CommonBtn size="small" @done="handleChoice(item)" :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }">
              <span v-html="getEquipName(item, item.type)"></span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>

    <el-dialog v-model="enchantResShow" width="100%" style="max-width: 500px">
      <h2 class="affix">
        <span class="grow">附魔成功</span>
      </h2>
      <template v-if="equipInfo">
        <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
          <template #reference>
            <div style="width: 50%; margin: 0 auto">
              <CommonBtn size="small" style="width: 100%">
                <span v-html="getEquipName(equipInfo, equipInfo.type)"></span>
              </CommonBtn>
            </div>
          </template>
          <EquipInfo :equip-info="equipInfo" :show-action="false" style="max-height: 60vh" />
        </el-popover>
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="enchantResShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.enchant {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  .enchant-table {
    position: relative;
    padding: 0 8px 10px 8px;
    margin-bottom: 10px;
    overflow: auto;
    .enchant-equip {
      border: 1px var(--fontColor) solid;
      padding: 6px 8px;
      border-radius: 4px;
      background-color: rgba($color: var(--fontColor), $alpha: 0.1);
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
  }
  .item-list {
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    justify-content: unset;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .cur-gold {
      width: 100%;
      text-align: right;
      font-size: 14px;
      padding-right: 10px;
      margin: 0;
    }
    .item-btn-wrap {
      overflow: auto;
    }
    &.package {
      flex: 1;
      min-height: 135px;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
}
</style>
